import React, { useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, FormField, LoadingModule,
} from '@domain-group/suit-theme'

import type {
  AgencyModel,
  BrandThemeModel,
  ProviderModel,
  PublicationModel,
  ServiceProductModel,
  TemplateGroupModel,
} from '@models/types'

import useAssignTemplatePagePermissions from '../../hooks/useAssignTemplatePagePermissionsModal'

import NoResults from '../NoResults'

type PayloadMappingKey = 'agencyIds'
  | 'brandThemeIds'
  | 'providerIds'
  | 'publicationIds'
  | 'serviceProductIds'
  | 'templateGroupIds'

type PayloadMapping = {
  [key in PayloadMappingKey]: {
    entities: string,
    filteredEntities: string,
    filteredEntitiesCount: string,
    loadEntityIds: string,
    payloadKey: string,
    requestOptions?: {
      [key: string]: boolean,
    },
  }
}

const payloadMapping: PayloadMapping = {
  agencyIds: {
    entities: 'agencies',
    filteredEntities: 'filteredAgencies',
    filteredEntitiesCount: 'filteredAgenciesCount',
    loadEntityIds: 'loadAgencyIds',
    payloadKey: 'agencies',
  },
  brandThemeIds: {
    entities: 'brandThemes',
    filteredEntities: 'filteredBrandThemes',
    filteredEntitiesCount: 'filteredBrandThemesCount',
    loadEntityIds: 'loadBrandThemeIds',
    payloadKey: 'brandThemes',
  },
  providerIds: {
    entities: 'providers',
    filteredEntities: 'filteredProviders',
    filteredEntitiesCount: 'filteredProvidersCount',
    loadEntityIds: 'loadProviderIds',
    payloadKey: 'providers',
  },
  publicationIds: {
    entities: 'publications',
    filteredEntities: 'filteredPublications',
    filteredEntitiesCount: 'filteredPublicationsCount',
    loadEntityIds: 'loadPublicationIds',
    payloadKey: 'publications',
  },
  serviceProductIds: {
    entities: 'serviceProducts',
    filteredEntities: 'filteredServiceProducts',
    filteredEntitiesCount: 'filteredServiceProductsCount',
    loadEntityIds: 'loadServiceProductIds',
    payloadKey: 'serviceProducts',
    requestOptions: {
      include_service_product_provider_ids: true,
    },
  },
  templateGroupIds: {
    entities: 'templateGroups',
    filteredEntities: 'filteredTemplateGroups',
    filteredEntitiesCount: 'filteredTemplateGroupsCount',
    loadEntityIds: 'loadTemplateGroupIds',
    payloadKey: 'templateGroups',
  },
}

type EntityListItemProps = {
  callbacks?: {
    onClick: () => void,
  },
  entity: AgencyModel
    | BrandThemeModel
    | ProviderModel
    | PublicationModel
    | ServiceProductModel
    | TemplateGroupModel,
  selected: boolean,
}

interface EntityListProps {
  assignTemplatePagePermissionsPayload: ReturnType<typeof useAssignTemplatePagePermissions>,
  entityListItemComponent: (props: EntityListItemProps) => React.ReactNode,
  entityKey: PayloadMappingKey,
}

const EntityList = (props: EntityListProps) => {
  const {
    assignTemplatePagePermissionsPayload: {
      callbacks: {
        bulkDeselectEntityIds,
        bulkSelectEntityIds,
        filterUnassignedEntities,
        filterVisibleEntities,
        setCurrentSegmentKey,
        toggleEntityId,
      },
      permissions: {
        queued: queuedPermissions,
      },
      payloads,
    },
    entityKey,
    entityListItemComponent,
  } = props

  // Payload Mapping
  const payloadKeys = payloadMapping[entityKey]

  // Entities Payload
  // @ts-expect-error TS7053
  const entitiesPayload = payloads[payloadKeys.payloadKey] || {}
  const {
    callbacks: entitiesPayloadCallbacks,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    loading,
    reduxActionPerformed,
  } = entitiesPayload

  const loadEntityIdsFn = entitiesPayloadCallbacks[payloadKeys.loadEntityIds]
  const filteredEntities = entitiesPayload[payloadKeys.filteredEntities]
  const filteredEntitiesCount = entitiesPayload[payloadKeys.filteredEntitiesCount]
  const entityObjects = entitiesPayload[payloadKeys.entities]
  const queuedPermissionIds = queuedPermissions[entityKey]

  // Visible & Unassigned
  const visibleEntities = filterVisibleEntities({
    entityKey,
    entities: filteredEntities,
  })
  // @ts-expect-error TS2339
  const visibleEntityIds = visibleEntities.map(p => p.id)
  const hasVisibleEntities = !!visibleEntities.length

  const unassignedEntities = filterUnassignedEntities({
    entityKey,
    entities: entityObjects,
  })

  // Load Unassigned
  useEffect(() => {
    if (reduxActionPerformed && unassignedEntities.length && loadEntityIdsFn){
      const loadEntityIdsParams = {
        // @ts-expect-error TS7006
        [entityKey]: unassignedEntities.map(p => p.id),
        entityOptions: payloadKeys.requestOptions ?? {},
      }

      loadEntityIdsFn(loadEntityIdsParams)
    }
  }, [reduxActionPerformed, unassignedEntities.length])

  const ListItemComponent = entityListItemComponent

  return (
    <>
      <LoadingModule boxProps={{ marginBottom: 0, marginTop: 'medium' }} loading={loading} />

      {!loading && !hasVisibleEntities && <NoResults callbacks={{ reset: () => setCurrentSegmentKey('all') }} />}

      {!loading && hasVisibleEntities && (
      <>
        <Box flexDirection="column" marginTop="medium" variant="white">
          {visibleEntities.map(entity => (
            <ListItemComponent
              // @ts-expect-error TS2339
              callbacks={{ onClick: () => toggleEntityId({ entityKey, entityId: entity.id }) }}
              // @ts-expect-error TS2339
              key={entity.id}
              entity={entity}
              // @ts-expect-error TS2345
              selected={queuedPermissionIds?.includes(entity.id)}
            />
          ))}
        </Box>

        <DashboardModule.LoadMoreFooter
          boxProps={{ paddingX: 0, paddingBottom: 0 }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredEntitiesCount}
          loading={loading}
          showBorderTop={false}
        >
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            onClick={() => bulkSelectEntityIds({
              entityKey,
              entityIds: visibleEntityIds,
            })}
            // @ts-expect-error
            marginLeft={canLoadMore ? 'medium' : 0}
            size="medium"
            width="auto"
          >
            Select Visible
          </Button>

          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTimesCircle} />}
            onClick={() => bulkDeselectEntityIds({
              entityKey,
              entityIds: visibleEntityIds,
            })}
            // @ts-expect-error
            marginLeft="medium"
            size="medium"
            width="auto"
          >
            Deselect Visible
          </Button>
        </DashboardModule.LoadMoreFooter>
      </>
      )}

      {!!unassignedEntities.length && (
        <FormField label="To Be Unassigned" marginTop="large">
          <Box flexDirection="column" variant="white">
            {/*
             // @ts-expect-error TS7006 */}
            {unassignedEntities.map(entity => (
              <ListItemComponent key={entity.id} entity={entity} selected={false} />
            ))}
          </Box>
        </FormField>
      )}
    </>
  )
}

export default EntityList
